import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {TabEvent} from 'notebook/mediocre/EditorTypes';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {LastSyncedUpstreamValue} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import {useTimedCellEnterHandler} from 'notebook/tables/core-ui/cell/useTimedCellEnterHandler';
import FormulaEditable from 'notebook/tables/core-ui/FormulaEditable';
import {TableEditableClass} from 'notebook/tables/core-ui/TableEditable';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {useCellModel} from 'notebook/tables/store/hooks/useCellModel';
import {Cell, ColumnConstraint, RowData, TableIndex} from 'notebook/tables/TableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

type CellIndex = [number, number]; // [i, j]

export interface TableCellEditorProps {
  // TODO(BNCH-113857): Remove this once redux is enabled
  // These are now all optional because they might come from Redux if it is enabled
  // These will be removed once we switch over to redux
  columnConstraint?: ColumnConstraint | null;
  cell?: Cell;
  evaluatedCell?: EvaluatedCell;
  rowData?: RowData | null;

  fileId: string;
  tableName: string;
  tableNamespace: string;
  getLastIndex: () => TableIndex;
  cid: string;
  i: number;
  j: number;

  // The width of the cell editor.
  width: number;

  // Only available if the cell is part of a StructuredTable and was previously submitted or refreshed
  lastSyncedUpstreamValue?: LastSyncedUpstreamValue;

  // "Stateful" props
  highlightClass?: string;

  // Actions
  storeState: TableEditableClass['saveMultiSelect'];
  commitCell: TableEditableClass['updateCell'];
  moveFromCell: TableEditableClass['moveFromCell'];

  // Event handlers
  cellEnterHandler: TableEditableClass['cellEnterHandler'];
  cellEscapeHandler: TableEditableClass['cellEscapeHandler'];
  // Set if cell should show the fill down square
  keyDownHook: TableEditableClass['hideHoverCell'];

  // Formulas
  disableFormulas?: boolean;
}

export function TableCellEditor({
  fileId,
  cid,
  i,
  j,
  width,
  lastSyncedUpstreamValue,
  highlightClass,
  tableNamespace,
  storeState,
  commitCell,
  getLastIndex,
  cellEnterHandler,
  cellEscapeHandler,
  keyDownHook,
  moveFromCell,
  tableName,
  disableFormulas,
  ...rest
}: TableCellEditorProps): JSX.Element {
  const {structuredTableKind} = StructuredTableContext.useContext();

  // TODO(BNCH-113857): Remove this once selectors are enabled
  // For now, cellModel may be null if selectors are disabled.  Pass along the props if so
  const {cell, evaluatedCell, columnConstraint = null, rowData = null} = useCellModel(j) ?? rest;

  const timedCellEnterHandler = useTimedCellEnterHandler({
    enabled: window.hasOwnProperty('Cypress') && cell != null,
    text: cell?.text!,
    cellEnterHandler,
  });

  assert(cell != null && evaluatedCell != null, 'Cell data must be provided');

  const index: CellIndex = [i, j];

  // Move right-down/left-up through rows.
  const handleFormulaTabKeyDown = (formula: string, {shiftKey}: TabEvent): void => {
    // Save selection to history before making change.
    storeState();

    // Update the current cell before leaving it
    commitCell(index, formula);
    moveFromCell({index, text: formula, dir: shiftKey ? 'left' : 'right', wrap: true});
  };

  const handleCellTabKeyDown = ({shiftKey}: ElementKeyboardEvent): void => {
    // Save selection to history before making change.
    storeState();
    moveFromCell({index, dir: shiftKey ? 'left' : 'right', wrap: true});
  };

  const handleFormulaArrowKeyDown = (formula: string, e: ElementKeyboardEvent): void => {
    // Must be collapsed, with no shift key.
    const selection = selutil.getSelectionIndices();
    if (e.shiftKey || !selection || selection.start !== selection.end) {
      return;
    }
    const cursor = selection.start;

    // Must not be a formula
    const formulaLength = formula.length;
    if (formulaLength && formula[0] === '=') {
      return;
    }

    // Determine new selection
    const dir = (() => {
      if (keyboard.isLeftKey(e) && cursor === 0) {
        return 'left';
      } else if (keyboard.isRightKey(e) && cursor === formulaLength) {
        return 'right';
      } else if (keyboard.isUpKey(e)) {
        return 'up';
      } else if (keyboard.isDownKey(e)) {
        return 'down';
      }
      return null;
    })();
    if (dir == null) {
      return;
    }

    // Update the cell
    commitCell(index, formula);
    moveFromCell({index, dir, text: formula, wrap: false});
  };

  return (
    <div
      className={classNames('mediocre-tableEditable-tableCellEditorCell', highlightClass)}
      style={{width}}
      role="cell"
      aria-current="true"
      data-cid={cid}
      data-namespace={tableNamespace}
      {...getTestProps({component: 'TableCellEditor', element: 'root'})}
    >
      <FormulaEditable
        cell={cell}
        evaluatedCell={evaluatedCell}
        fileId={fileId}
        tableName={tableName}
        tableNamespace={tableNamespace}
        getLastIndex={getLastIndex}
        formulaArrowKeyDown={handleFormulaArrowKeyDown}
        formulaTabKeyDown={handleFormulaTabKeyDown}
        cellTabKeyDown={handleCellTabKeyDown}
        onFormulaFinalize={timedCellEnterHandler}
        onFormulaFocus={() => {}}
        keyDownHook={keyDownHook}
        escapeHandler={cellEscapeHandler}
        index={index}
        cid={cid}
        columnDefinition={columnConstraint && columnConstraint.columnDefinition}
        isFormulaBox={false}
        structuredTableKind={structuredTableKind}
        lastSyncedUpstreamValue={lastSyncedUpstreamValue}
        rowMetadata={rowData?.rowMetadata ?? null}
        disableFormulas={disableFormulas}
      />
    </div>
  );
}
